/* Progressive enhancement for content pages. Reading never depends on GSAP. */
(() => {
  // Persist explicit pause choices across bfcache and section reinitialization.
  const userPaused = new WeakSet();
  const policyPaused = new WeakSet();
  const trackedMedia = new WeakSet();
  const revealed = new WeakSet();

  function trackPauseIntent(video) {
    if (trackedMedia.has(video)) return;
    trackedMedia.add(video);
    // Media events are queued. Keep their source tracking alive during cleanup,
    // so a policy pause cannot be mistaken for user input by the next runtime.
    video.addEventListener('pause', () => {
      if (policyPaused.has(video)) policyPaused.delete(video);
      else userPaused.add(video);
    });
    video.addEventListener('play', () => userPaused.delete(video));
  }

  function initialize() {
    window.__kmContentCleanup?.();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const videos = Array.from(document.querySelectorAll('video[data-km-ambient-video]'));
    const controller = new AbortController();
    const { signal } = controller;
    const visible = new Set();
    let motionContext;
    let splits = [];
    let disposed = false;
    let fontsReadyForMotion = false;
    let resizeTimer;
    let previousWidth = window.innerWidth;

    function pauseMedia(video) {
      if (!video.paused) {
        policyPaused.add(video);
        video.pause();
      }
    }
    function updateMedia(video) {
      const shouldPlay = visible.has(video) && !document.hidden &&
        !reduceMotion.matches && !navigator.connection?.saveData && !userPaused.has(video);
      if (!shouldPlay) {
        pauseMedia(video);
        return;
      }
      if (video.paused && !video.error) {
        const request = video.play();
        request?.catch(() => { /* Native controls remain available if autoplay is denied. */ });
      }
    }
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting, intersectionRatio }) => {
        if (isIntersecting && intersectionRatio >= 0.15) visible.add(target);
        else visible.delete(target);
        updateMedia(target);
      });
    }, { threshold: 0.15 }) : null;
    videos.forEach((video) => {
      trackPauseIntent(video);
      video.addEventListener('play', () => {
        if (document.hidden || (observer && !visible.has(video))) pauseMedia(video);
      }, { signal });
      // Keep the original frame and all adjacent explanatory text when media fails.
      video.addEventListener('error', () => {
        pauseMedia(video);
        video.dataset.kmMediaState = 'unavailable';
      }, { signal });
      observer?.observe(video);
    });
    document.addEventListener('visibilitychange', () => videos.forEach(updateMedia), { signal });

    function resetMotion() {
      document.querySelectorAll('.animated-title, .animated-text, .animated-media').forEach((node) => {
        if (node.getBoundingClientRect().top < window.innerHeight) revealed.add(node);
      });
      motionContext?.revert();
      motionContext = undefined;
      splits.forEach((split) => split.revert());
      splits = [];
    }
    function animateContent() {
      if (disposed || !fontsReadyForMotion || motionContext || reduceMotion.matches ||
        !window.gsap || !window.ScrollTrigger || !window.SplitText) return;
      const { gsap, ScrollTrigger, SplitText } = window;
      gsap.registerPlugin(ScrollTrigger, SplitText);
      motionContext = gsap.context(() => {
        function reveal(nodes, container, options) {
          if (!nodes.length || revealed.has(container)) return;
          gsap.fromTo(nodes, options.from, {
            ...options.to,
            ease: 'power2.out',
            onComplete: () => revealed.add(container),
            scrollTrigger: { trigger: container, start: 'top 90%', once: true },
          });
        }
        document.querySelectorAll('.animated-title').forEach((container) => {
          if (revealed.has(container) || container.querySelector('a, button, input, select, textarea')) return;
          const split = new SplitText(container, {
            type: 'lines,words', linesClass: 'title-line', wordsClass: 'km-title-word',
            ignore: '.km-outlined-word', reduceWhiteSpace: false, aria: 'none',
          });
          splits.push(split);
          const words = Array.from(container.querySelectorAll('.km-title-word, .km-outlined-word'))
            .filter((word) => !word.matches('.km-outlined-word') || !word.closest('.km-title-word'));
          reveal(words, container, { from: { y: '110%' }, to: { y: '0%', duration: 0.8, stagger: 0.1 } });
        });
        document.querySelectorAll('.animated-text').forEach((container) => {
          if (revealed.has(container) || container.querySelector('a, button, input, select, textarea')) return;
          const split = new SplitText(container, { type: 'lines', aria: 'none' });
          splits.push(split);
          reveal(split.lines, container, { from: { opacity: 0, y: '100%' }, to: { opacity: 1, y: 0, duration: 0.8, stagger: 0.1 } });
        });
        document.querySelectorAll('.animated-media').forEach((media) => {
          reveal([media], media, { from: { opacity: 0, y: '25%' }, to: { opacity: 1, y: '0%', duration: 1 } });
        });
      }, document.body);
    }
    function onPreferenceChange() {
      resetMotion();
      videos.forEach(updateMedia);
      animateContent();
    }
    reduceMotion.addEventListener('change', onPreferenceChange, { signal });
    window.addEventListener('resize', () => {
      if (Math.abs(window.innerWidth - previousWidth) < 32) return;
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        previousWidth = window.innerWidth;
        resetMotion();
        animateContent();
      }, 150);
    }, { signal });
    // Avoid splitting on a fallback font, and do not hide text for a delayed font.
    const fontsReady = document.fonts ? document.fonts.ready.then(() => true) : Promise.resolve(true);
    Promise.race([fontsReady, new Promise((resolve) => setTimeout(() => resolve(false), 2000))])
      .then((ready) => {
        if (disposed) return;
        fontsReadyForMotion = ready;
        animateContent();
      });
    window.__kmContentCleanup = () => {
      disposed = true;
      clearTimeout(resizeTimer);
      controller.abort();
      observer?.disconnect();
      videos.forEach(pauseMedia);
      resetMotion();
    };
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
  window.addEventListener('pagehide', () => window.__kmContentCleanup?.());
  window.addEventListener('pageshow', (event) => { if (event.persisted) initialize(); });
  document.addEventListener('shopify:section:load', initialize);
})();
